/**
 * Thread-state contexts for the LLM transcript render.
 *
 * The transport's `TranscriptProvider` mounts these around the one-tree
 * transcript render (`renderThreadTranscript`), so every component in the
 * tree — shared record sections and per-tool formatter JSX alike —
 * derives its own visibility from live thread state instead of receiving
 * precomputed flags. Outside a transcript render (the Show-details
 * panel, tool descriptions) both contexts are absent and every hook
 * falls back to full, current-turn rendering.
 *
 * Typed over structural message/part slices (the `RevisionScanMessage`
 * pattern) so `lib/tool` stays decoupled from the transport's concrete
 * thread types; live `AtlasMessage`s satisfy the slices structurally.
 */

import { createContext, use } from 'react';

import type { ToolSidecarEntry } from './message-formatters.ts';
import type { RevisionIndex } from './revisions.ts';
import { isTerminalToolPart, type SettledToolPart, settledOutcome } from './settled-outcome.ts';

/** Structural slice of a thread tool part the transcript hooks read. */
export type TranscriptToolPart = SettledToolPart & {
  readonly type?: string;
  readonly toolCallId?: string;
};

/** Structural slice of a thread message the transcript hooks scan. */
export type TranscriptThreadMessage = {
  readonly role?: string;
  readonly parts?: readonly TranscriptToolPart[];
};

export type TranscriptThread = {
  readonly messages: readonly TranscriptThreadMessage[];
  /** Revision projection derived from `messages` by the provider, once per transcript render. */
  readonly revisions: RevisionIndex;
  readonly toolRegistry: Record<string, ToolSidecarEntry>;
  /** Chronologically last successful revisable result — the one record carrying revision coaching. Derived once by the provider. */
  readonly latestRevisableCallId: string | undefined;
};

/** The thread being rendered to model messages; `null` outside a transcript render. */
export const TranscriptContext = createContext<TranscriptThread | null>(null);

/** The tool part whose record is currently rendering; `null` outside a tool record. */
export const ToolRecordContext = createContext<TranscriptToolPart | null>(null);

/** A part is a tool record when it carries the call/state pair every tool part exposes. */
const isToolRecordPart = (part: TranscriptToolPart): boolean =>
  part.toolCallId !== undefined && part.state !== undefined;

const lastTerminalToolCallId = (messages: readonly TranscriptThreadMessage[]): string | undefined =>
  messages
    .flatMap((m) => m.parts ?? [])
    .filter(isToolRecordPart)
    .findLast(isTerminalToolPart)?.toolCallId;

/** Whether the part sits after the last user prompt. With no user message every record counts as current. */
const isCurrentTurn = (messages: readonly TranscriptThreadMessage[], toolCallId: string): boolean => {
  const lastUserIdx = messages.findLastIndex((m) => m.role === 'user');
  const partIdx = messages.findIndex((m) => (m.parts ?? []).some((p) => p.toolCallId === toolCallId));
  return partIdx > lastUserIdx;
};

/**
 * Whether the currently-rendering tool record should render compact —
 * the default policy SSOT every compact-capable renderer reads:
 *
 * - **failures** compact unless they are the chronologically last
 *   terminal record (anti-flail: the model only acts on the latest
 *   result, and recovery prose on every prior failure drowns out its
 *   sense of progress);
 * - **successes** compact unless they belong to the current turn
 *   (everything after the last user prompt stays full while the agent
 *   works);
 * - denials and in-flight calls never compact;
 * - outside a transcript render, or outside a tool record: never
 *   compact — standalone renders (Show-details panel) always show the
 *   full prose.
 */
export function useCompact(): boolean {
  const thread = use(TranscriptContext);
  const part = use(ToolRecordContext);
  if (thread === null || part === null || part.toolCallId === undefined) {
    return false;
  }
  const outcome = settledOutcome(part);
  if (outcome === null) {
    return false;
  }
  if (outcome.kind === 'error') {
    return lastTerminalToolCallId(thread.messages) !== part.toolCallId;
  }
  if (outcome.kind === 'result') {
    return !isCurrentTurn(thread.messages, part.toolCallId);
  }
  return false;
}
